import { useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Server, Monitor, Laptop, Router, Wifi, HardDrive, Printer, Phone, Shield, X, ChevronDown, MoreVertical, Edit, Trash2, MapPin, Network, AlertTriangle, Lock, Building2, Globe } from 'lucide-react';
import { documentationAPI, adminCompanyAPI } from '../../services/api';
import { useAuthStore } from '../../context/authStore';
import { isInternalRole } from '../../utils/roles.js';

// Icon mapping for configuration types
const TYPE_ICONS = {
  'Server': Server,
  'Workstation': Monitor,
  'Laptop': Laptop,
  'Firewall': Shield,
  'Switch': Network,
  'Router': Router,
  'Wireless AP': Wifi,
  'NAS/Storage': HardDrive,
  'Printer': Printer,
  'Phone/VoIP': Phone,
  default: Server
};

function getTypeIcon(typeName) {
  return TYPE_ICONS[typeName] || TYPE_ICONS.default;
}

// Visibility badge component
function VisibilityBadge({ visibility }) {
  const config = {
    private: { icon: Lock, label: 'Internal', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
    company: { icon: Building2, label: 'Company', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
    public: { icon: Globe, label: 'Public', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  };
  const { icon: Icon, label, className } = config[visibility] || config.private;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${className}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// Status badge
function StatusBadge({ status }) {
  const statusConfig = {
    'Active': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    'Inactive': 'bg-neutral-100 text-neutral-700 dark:bg-neutral-700 dark:text-neutral-300',
    'Retired': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig[status] || statusConfig['Active']}`}>
      {status || 'Active'}
    </span>
  );
}

// Configuration modal for create/edit
function ConfigurationModal({ configuration, companyId, isInternal, onClose }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: configuration?.name || '',
    hostname: configuration?.hostname || '',
    configurationTypeId: configuration?.configuration_type_id || '',
    configurationStatus: configuration?.configuration_status || 'Active',
    primaryIp: configuration?.primary_ip || '',
    primaryMac: configuration?.primary_mac || '',
    manufacturer: configuration?.manufacturer || '',
    model: configuration?.model || '',
    serialNumber: configuration?.serial_number || '',
    assetTag: configuration?.asset_tag || '',
    operatingSystem: configuration?.operating_system || '',
    osVersion: configuration?.os_version || '',
    locationId: configuration?.location_id || '',
    warrantyExpiration: configuration?.warranty_expiration?.split('T')[0] || '',
    notes: configuration?.notes || '',
    visibility: configuration?.visibility || 'company',
    companyId: configuration?.company_id || companyId || '',
  });
  const [error, setError] = useState(null);

  // Fetch companies for internal staff
  const { data: companiesData } = useQuery({
    queryKey: ['admin', 'companies', 'list'],
    queryFn: () => adminCompanyAPI.list({ limit: 500 }),
    enabled: isInternal,
  });
  const companies = companiesData?.data?.companies || [];

  // Fetch configuration types
  const { data: typesData } = useQuery({
    queryKey: ['documentation', 'configuration-types'],
    queryFn: () => documentationAPI.getConfigurationTypes(),
  });

  // Fetch locations - use selected company for internal staff
  const selectedCompanyId = isInternal ? formData.companyId : companyId;
  const { data: locationsData } = useQuery({
    queryKey: ['documentation', 'locations', 'select', selectedCompanyId],
    queryFn: () => documentationAPI.getLocationsForSelect(selectedCompanyId),
    enabled: !!selectedCompanyId,
  });

  const types = typesData?.data?.types || [];
  const locations = locationsData?.data?.locations || [];

  const createMutation = useMutation({
    mutationFn: (data) => documentationAPI.createConfiguration({ ...data, companyId: data.companyId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'configurations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to create configuration'),
  });

  const updateMutation = useMutation({
    mutationFn: (data) => documentationAPI.updateConfiguration(configuration.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'configurations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to update configuration'),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!configuration && !formData.companyId) {
      setError('Company is required');
      return;
    }
    if (configuration) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-700 sticky top-0 bg-white dark:bg-neutral-800">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
            {configuration ? 'Edit Configuration' : 'New Configuration'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Company selector for internal staff */}
          {isInternal && !configuration && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Company *</label>
              <select
                value={formData.companyId}
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value ? parseInt(e.target.value) : '', locationId: '' })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                required
              >
                <option value="">Select a company...</option>
                {companies.map(company => (
                  <option key={company.id} value={company.cw_company_id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., DC01"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Hostname</label>
              <input
                type="text"
                value={formData.hostname}
                onChange={(e) => setFormData({ ...formData, hostname: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., dc01.domain.local"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Type</label>
              <select
                value={formData.configurationTypeId}
                onChange={(e) => setFormData({ ...formData, configurationTypeId: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Select type...</option>
                {types.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Status</label>
              <select
                value={formData.configurationStatus}
                onChange={(e) => setFormData({ ...formData, configurationStatus: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Retired">Retired</option>
              </select>
            </div>
          </div>

          {/* Network Info */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Network Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Primary IP</label>
                <input
                  type="text"
                  value={formData.primaryIp}
                  onChange={(e) => setFormData({ ...formData, primaryIp: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  placeholder="192.168.1.100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Primary MAC</label>
                <input
                  type="text"
                  value={formData.primaryMac}
                  onChange={(e) => setFormData({ ...formData, primaryMac: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  placeholder="00:1A:2B:3C:4D:5E"
                />
              </div>
            </div>
          </div>

          {/* Hardware Info */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
            <h3 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">Hardware Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Manufacturer</label>
                <input
                  type="text"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  placeholder="e.g., Dell, HP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                  placeholder="e.g., PowerEdge R740"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Serial Number</label>
                <input
                  type="text"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Asset Tag</label>
                <input
                  type="text"
                  value={formData.assetTag}
                  onChange={(e) => setFormData({ ...formData, assetTag: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
          </div>

          {/* OS Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Operating System</label>
              <input
                type="text"
                value={formData.operatingSystem}
                onChange={(e) => setFormData({ ...formData, operatingSystem: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
                placeholder="e.g., Windows Server 2022"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">OS Version</label>
              <input
                type="text"
                value={formData.osVersion}
                onChange={(e) => setFormData({ ...formData, osVersion: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Location & Warranty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Location</label>
              <select
                value={formData.locationId}
                onChange={(e) => setFormData({ ...formData, locationId: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              >
                <option value="">Select location...</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name}{loc.is_primary ? ' (Primary)' : ''}{loc.city ? ` - ${loc.city}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Warranty Expiration</label>
              <input
                type="date"
                value={formData.warrantyExpiration}
                onChange={(e) => setFormData({ ...formData, warrantyExpiration: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Visibility</label>
            <select
              value={formData.visibility}
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500"
            >
              <option value="private">Internal Only</option>
              <option value="company">Visible to Company</option>
              <option value="public">Public</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 text-white rounded-lg"
            >
              {isLoading ? 'Saving...' : configuration ? 'Save Changes' : 'Create Configuration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Configuration card component
function ConfigurationCard({ config, isInternal, onEdit, onDelete }) {
  const [showMenu, setShowMenu] = useState(false);
  const TypeIcon = getTypeIcon(config.type_name);

  const warrantyDaysLeft = config.warranty_expiration
    ? Math.ceil((new Date(config.warranty_expiration) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div className="group p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 hover:border-brand-300 dark:hover:border-brand-600 hover:shadow-md transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`p-2 rounded-lg ${config.type_color ? `bg-${config.type_color}-100 dark:bg-${config.type_color}-900/30` : 'bg-neutral-100 dark:bg-neutral-700'}`}>
            <TypeIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
          </div>
          <div className="flex-1 min-w-0">
            <Link
              to={`/documentation/configurations/${config.id}`}
              className="font-medium text-neutral-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 truncate block"
            >
              {config.name}
            </Link>
            {config.hostname && (
              <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">{config.hostname}</p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {config.type_name && (
                <span className="text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-2 py-0.5 rounded">
                  {config.type_name}
                </span>
              )}
              <StatusBadge status={config.configuration_status} />
              {isInternal && <VisibilityBadge visibility={config.visibility} />}
            </div>
          </div>
        </div>
        <div className="flex items-start gap-2">
          {warrantyDaysLeft !== null && warrantyDaysLeft <= 90 && (
            <div className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
              warrantyDaysLeft <= 0
                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                : warrantyDaysLeft <= 30
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
            }`}>
              <AlertTriangle className="w-3 h-3" />
              {warrantyDaysLeft <= 0 ? 'Expired' : `${warrantyDaysLeft}d`}
            </div>
          )}
          {isInternal && (
            <div className="relative">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="p-1 opacity-0 group-hover:opacity-100 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-opacity"
              >
                <MoreVertical className="w-4 h-4 text-neutral-500" />
              </button>
              {showMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[120px]">
                    <button
                      onClick={() => { setShowMenu(false); onEdit(config); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <Edit className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      onClick={() => { setShowMenu(false); onDelete(config); }}
                      className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
        {config.primary_ip && (
          <span className="flex items-center gap-1">
            <Network className="w-3 h-3" />
            {config.primary_ip}
          </span>
        )}
        {config.location_name && (
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {config.location_name}
          </span>
        )}
        {config.manufacturer && config.model && (
          <span className="truncate">
            {config.manufacturer} {config.model}
          </span>
        )}
      </div>
    </div>
  );
}

// Delete confirmation modal
function DeleteConfirmModal({ config, onClose }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const deleteMutation = useMutation({
    mutationFn: () => documentationAPI.deleteConfiguration(config.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation', 'configurations'] });
      onClose();
    },
    onError: (err) => setError(err.response?.data?.error || 'Failed to delete configuration'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">Delete Configuration?</h2>
        <p className="text-neutral-600 dark:text-neutral-400 mb-4">
          Are you sure you want to delete "{config.name}"? This action cannot be undone.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
          >
            Cancel
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg"
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ConfigurationsPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || '');
  const [showModal, setShowModal] = useState(false);
  const [configToEdit, setConfigToEdit] = useState(null);
  const [configToDelete, setConfigToDelete] = useState(null);
  const { user } = useAuthStore();

  const isInternal = isInternalRole(user?.role);
  const companyId = user?.cw_company_id;

  // Fetch configuration types
  const { data: typesData } = useQuery({
    queryKey: ['documentation', 'configuration-types'],
    queryFn: () => documentationAPI.getConfigurationTypes(),
  });

  // Fetch configurations
  const debouncedSearch = useDebounce(search, 300);
  const { data: configData, isLoading } = useQuery({
    queryKey: ['documentation', 'configurations', { search: debouncedSearch, typeId: selectedType, companyId: isInternal ? undefined : companyId }],
    queryFn: () => documentationAPI.listConfigurations({
      search: debouncedSearch || undefined,
      typeId: selectedType || undefined,
      companyId: isInternal ? undefined : companyId,
      limit: 50
    }),
  });

  const types = typesData?.data?.types || [];
  const configurations = configData?.data?.configurations || [];
  const pagination = configData?.data?.pagination;

  const handleNewConfig = () => {
    setConfigToEdit(null);
    setShowModal(true);
  };

  const handleEditConfig = (config) => {
    setConfigToEdit(config);
    setShowModal(true);
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Configurations</h1>
          <p className="text-neutral-600 dark:text-neutral-400">
            {isInternal ? 'Manage device and asset configurations' : 'View your devices and assets'}
          </p>
        </div>
        {isInternal && (
          <button
            onClick={handleNewConfig}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Configuration
          </button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search configurations..."
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>
        <div className="relative">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="appearance-none pl-4 pr-10 py-2.5 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            {types.map(type => (
              <option key={type.id} value={type.id}>{type.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
        </div>
      </div>

      {/* Configurations Grid */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="p-4 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 animate-pulse">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-neutral-200 dark:bg-neutral-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : configurations.length > 0 ? (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {configurations.map(config => (
              <ConfigurationCard
                key={config.id}
                config={config}
                isInternal={isInternal}
                onEdit={handleEditConfig}
                onDelete={setConfigToDelete}
              />
            ))}
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="mt-6 text-center text-sm text-neutral-600 dark:text-neutral-400">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} configurations)
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <Server className="w-12 h-12 mx-auto text-neutral-300 dark:text-neutral-600 mb-4" />
          <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
            {search ? 'No configurations found' : 'No configurations yet'}
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400 mb-4">
            {search
              ? 'Try adjusting your search terms'
              : isInternal
                ? 'Create your first configuration to get started'
                : 'No devices have been documented yet'
            }
          </p>
          {isInternal && !search && (
            <button
              onClick={handleNewConfig}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Configuration
            </button>
          )}
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ConfigurationModal
          configuration={configToEdit}
          companyId={companyId}
          isInternal={isInternal}
          onClose={() => { setShowModal(false); setConfigToEdit(null); }}
        />
      )}

      {configToDelete && (
        <DeleteConfirmModal
          config={configToDelete}
          onClose={() => setConfigToDelete(null)}
        />
      )}
    </div>
  );
}
